import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  ChevronRight, BookOpen, Search, Link2, ArrowUp, Printer,
  Info, TriangleAlert, Monitor, Smartphone, Apple,
} from 'lucide-react';
import ModuleShowcase from '../components/ModuleShowcase';
import ModuleGallery from '../components/ModuleGallery';
import AndroidShowcase from '../components/AndroidShowcase';

/* The manual's spine. `num` is split out of the label because the section
   number is what people quote to each other ("see 7.2"), so it earns its
   own chip in the rail and in the section head rather than being buried in
   a string. Order here IS the reading order and the scroll-spy order. */
/* `platform` decides which tab a section belongs to. 'both' sections —
   safety, troubleshooting, glossary — are true whichever client you use,
   so they appear under Web and Android alike rather than being duplicated
   or arbitrarily filed under one.

   The numbers do NOT restart per tab. They are the manual's canonical
   numbering and the prose cross-references them ("see section 4"), so 7
   has to mean the Android app no matter which tab is open. */
const TOC = [
  { id: 'intro',           num: 1,  label: 'Introduction',              platform: 'both' },
  { id: 'getting-started', num: 2,  label: 'Getting Started',           platform: 'both' },
  { id: 'modules',         num: 3,  label: 'Module Reference',          platform: 'web' },
  { id: 'web-admins',      num: 4,  label: 'Web — Admins & Managers',   platform: 'web' },
  { id: 'extra-fields',    num: 5,  label: 'Extra Fields on Leads',     platform: 'web' },
  { id: 'integrations',    num: 6,  label: 'Working with Other Systems', platform: 'web' },
  { id: 'phone-agents',    num: 7,  label: 'Android App',               platform: 'android' },
  { id: 'safety',          num: 8,  label: 'Staying Safe',              platform: 'both' },
  { id: 'troubleshoot',    num: 9,  label: 'Troubleshooting',           platform: 'both' },
  { id: 'examples',        num: 10, label: 'Real-Life Examples',        platform: 'web' },
  { id: 'permissions',     num: 11, label: 'Who Can Do What',           platform: 'both' },
  { id: 'glossary',        num: 12, label: 'Word List',                 platform: 'both' },
  { id: 'custom-module',   num: 13, label: 'Custom Module',             platform: 'web' },
  { id: 'webhooks',        num: 14, label: 'Webhooks & Integrations',   platform: 'web' },
];

const PLATFORMS = [
  { id: 'web',     label: 'Web',     icon: Monitor,    note: 'Dashboard' },
  { id: 'android', label: 'Android', icon: Smartphone, note: 'Agent app' },
  { id: 'ios',     label: 'iOS',     icon: Apple,      note: 'Coming soon', soon: true },
];

/* The iOS tab is honest rather than empty: it says what is not built yet
   and points at the two clients that are, instead of showing a blank
   manual and letting the reader wonder whether it failed to load. */
const IosComingSoon = () => (
  <div className="ios-soon">
    <span className="ios-soon__icon" aria-hidden="true">
      <Apple className="h-7 w-7" />
    </span>
    <h2 className="ios-soon__title">The iOS app is on the way</h2>
    <p className="ios-soon__body">
      WASLX runs on the web dashboard and on Android today. An iPhone build is on the roadmap, and
      beta customers get it first — there is nothing to document here yet, and we would rather say
      so than show you an empty manual.
    </p>
    <p className="ios-soon__body">
      Agents on iPhone can use the web dashboard in Safari in the meantime; everything except
      device-SIM dialling works there.
    </p>
    <div className="ios-soon__actions">
      <Link to="/contact" className="btn-pill btn-pill--primary btn-pill--sm">
        Ask about the iOS beta
      </Link>
    </div>
  </div>
);

/* Hue per section. Adjacent sections take DIFFERENT hues rather than shades
   of one — the multicolour rule from the design guide — and the cycle is
   the same .accent-N order the chart series uses, so a section keeps its
   colour between the rail, the head chip and the module gallery. */
const accentFor = (num) => `accent-${((num - 1) % 8) + 1}`;

/* ── shared style primitives ── */
/* Section head — the dashboard's .card-head, with the number promoted to a
   tinted chip. The heading text still arrives as "7. Title" from the body
   below, so the number is parsed out here rather than being duplicated at
   every one of the fourteen call sites. */
const H2 = ({ id, children }) => {
  const match = typeof children === 'string' ? children.match(/^(\d+)\.\s+([\s\S]+)$/) : null;
  const num = match ? Number(match[1]) : null;
  const title = match ? match[2] : children;

  return (
    <header id={id} className={`docs-section-head ${num ? accentFor(num) : 'accent-brand'}`}>
      {num !== null && <span className="docs-section-head__index text-lg" aria-hidden="true">{num}</span>}
      <h2 className="docs-section-head__title text-2xl">{title}</h2>
      <a href={`#${id}`} className="docs-anchor focus-ring" aria-label={`Link to ${title}`}>
        <Link2 className="h-4 w-4" />
      </a>
    </header>
  );
};
const H3 = ({ children }) => (
  <h3 className="mb-3 mt-7 text-lg font-bold text-content">{children}</h3>
);
const H4 = ({ children }) => (
  <h4 className="mb-2 mt-5 font-bold text-content">{children}</h4>
);
const P = ({ children }) => (
  <p className="mb-4 leading-relaxed text-content-muted">{children}</p>
);
const UL = ({ items }) => (
  <ul className="mb-4 space-y-1.5 pl-5">
    {items.map((item, i) => (
      <li key={i} className="list-disc leading-relaxed text-content-muted marker:text-primary">
        {item}
      </li>
    ))}
  </ul>
);
const OL = ({ items }) => (
  <ol className="mb-4 space-y-1.5 pl-5">
    {items.map((item, i) => (
      <li key={i} className="list-decimal leading-relaxed text-content-muted marker:font-bold marker:text-primary">
        {item}
      </li>
    ))}
  </ol>
);

/* Callouts. The glyph holder goes WHITE on the tint rather than a deeper
   shade of it — tint-on-tint is where these disappear, which is the same
   rule .card--tint .icon-tile follows in the design system. */
const Note = ({ children }) => (
  <div className="docs-callout accent-info">
    <span className="docs-callout__glyph" aria-hidden="true"><Info className="h-4 w-4" /></span>
    <div className="docs-callout__body">{children}</div>
  </div>
);
const Warn = ({ children }) => (
  <div className="docs-callout accent-warning">
    <span className="docs-callout__glyph" aria-hidden="true"><TriangleAlert className="h-4 w-4" /></span>
    <div className="docs-callout__body">{children}</div>
  </div>
);
const Code = ({ children }) => (
  <code className="rounded-sm bg-primary-tint px-1.5 py-0.5 font-mono text-sm text-primary-strong">
    {children}
  </code>
);

/* A table sits on the paper, not on the canvas, so it separates with a
   hairline and a well-filled header row — a second shadow on white reads
   as mud. Row separation stays; only the outer ring changes. */
const Table = ({ head, rows }) => (
  <div className="mb-6 overflow-x-auto rounded-xs border border-line">
    <table className="w-full text-[0.9375rem]">
      <thead>
        <tr className="border-b border-line bg-well">
          {head.map((h, i) => (
            <th
              key={i}
              className="whitespace-nowrap px-4 py-3 text-left text-2xs font-bold uppercase tracking-[0.08em] text-content-muted"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-b border-line last:border-0">
            {row.map((cell, ci) => (
              <td key={ci} className="px-4 py-3 align-top leading-relaxed text-content-muted">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* Sections whose body collapses. Everything from the Android app onward stays
   open — its screenshots are the reference people come for. */
const COLLAPSIBLE = new Set([
  'intro', 'getting-started', 'modules', 'web-admins', 'extra-fields', 'integrations',
]);

/* A numbered manual section with a collapsible body.

   The heading always stays in the DOM: it carries the section id, which is the
   scroll target for the sidebar rail and for #deep-links, so collapsing it
   would break both. Only the body comes and goes. */
const Section = ({ id, title, open, onToggle, children }) => {
  const match = typeof title === 'string' ? title.match(/^(\d+)\.\s+([\s\S]+)$/) : null;
  const num = match ? Number(match[1]) : null;
  const label = match ? match[2] : title;
  const panelId = `${id}-body`;

  return (
    <>
      <header id={id} className={`docs-section-head ${num ? accentFor(num) : 'accent-brand'}`}>
        {/* The anchor stays a sibling of the button — an <a> inside a <button>
            is invalid and browsers disagree about which one a click activates. */}
        <button
          type="button"
          className="docs-section-head__toggle focus-ring"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => onToggle(id)}
        >
          {num !== null && (
            <span className="docs-section-head__index text-lg" aria-hidden="true">{num}</span>
          )}
          <h2 className="docs-section-head__title text-2xl">{label}</h2>
          <ChevronRight
            className={`docs-section-head__caret${open ? ' is-open' : ''}`}
            aria-hidden="true"
          />
        </button>
        <a href={`#${id}`} className="docs-anchor focus-ring" aria-label={`Link to ${label}`}>
          <Link2 className="h-4 w-4" />
        </a>
      </header>
      {open && <div id={panelId}>{children}</div>}
    </>
  );
};

/* Kept as a spacer only. Every section now opens with .docs-section-head,
   which carries its own rule and leading space — a second <hr> immediately
   above it read as a double line. */
const Hr = () => <div className="h-2" aria-hidden="true" />;

/* ── main page ── */
const Documentation = () => {
  const [platform, setPlatform] = useState('web');
  const [activeId, setActiveId] = useState(TOC[0].id);
  const [query, setQuery]       = useState('');
  const [progress, setProgress] = useState(0);
  /* Collapsed by default — the manual is long, and a reader arrives looking for
     one section, not all fourteen. */
  const [openSections, setOpenSections] = useState(() => new Set());
  const contentRef = useRef(null);

  /* One rAF-throttled pass drives three things: the active rail row, the
     seam under the navbar and the rail's own meter. The manual is ~32,000px
     long, so three independent scroll listeners would be three layout reads
     per frame for the same number. */
  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;

      const el = contentRef.current;
      if (el) {
        /* rect + scrollY rather than offsetTop: offsetTop is measured from
           the offsetParent, so it would silently become wrong the day
           anything above the paper gains `position: relative`. */
        const rect = el.getBoundingClientRect();
        const span = rect.height - window.innerHeight;
        const done = span > 0 ? -rect.top / span : 1;
        setProgress(Math.min(100, Math.max(0, done * 100)));
      }

      /* Walk backwards: the active section is the LAST head that has
         crossed the sticky header, not the first one still on screen. */
      for (let i = TOC.length - 1; i >= 0; i--) {
        const node = document.getElementById(TOC[i].id);
        if (node && node.getBoundingClientRect().top <= 140) {
          setActiveId(TOC[i].id);
          return;
        }
      }
      setActiveId(TOC[0].id);
    };

    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure); };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    measure();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const isSectionOpen = (id) => openSections.has(id);

  const toggleSection = (id) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /* Landing on a collapsed section would show a heading and nothing else, so a
     rail click or a shared #link expands it on the way. */
  const scrollTo = (id) => {
    if (COLLAPSIBLE.has(id)) setOpenSections((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.replace('#', ''));
    if (hash && COLLAPSIBLE.has(hash)) {
      setOpenSections((prev) => new Set(prev).add(hash));
    }
  }, []);

  /* A collapsed body is not in the document, so printing would silently drop
     half the manual. Expand everything first — for the toolbar button, and for
     a Ctrl/Cmd-P the page never sees. */
  useEffect(() => {
    const expandAll = () => setOpenSections(new Set(COLLAPSIBLE));
    window.addEventListener('beforeprint', expandAll);
    return () => window.removeEventListener('beforeprint', expandAll);
  }, []);

  const handlePrint = () => {
    setOpenSections(new Set(COLLAPSIBLE));
    // Two frames: one for React to commit, one for layout to settle.
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  };

  const activeIndex = Math.max(0, TOC.findIndex(t => t.id === activeId));
  const active      = TOC[activeIndex];

  /* Filter, not search: fourteen labels fit in a rail, so matching the
     label is enough and a full-text index would be a lie about what this
     box actually does. */
  const q       = query.trim().toLowerCase();
  /* One predicate drives BOTH the rail and which sections render, so the
     contents list can never advertise a section the page does not show —
     which is exactly what happened when the two were decided separately. */
  const inPlatform = t =>
    platform !== 'ios' && (t.platform === platform || t.platform === 'both');
  const show = id => inPlatform(TOC.find(t => t.id === id) || {});
  const visible = TOC.filter(inPlatform).filter(t =>
    t.label.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="page-shell docs-page">
      <SEO
        title="Documentation & User Manual v1.3"
        description="Complete WASLX user guide for admins and agents. Learn how to set up campaigns, manage leads, monitor agents live, and use the Android app. Full 13-section manual."
        path="/documentation"
      />
      <Navbar />

      {/* ── Console header ──────────────────────────────────────────────
          The dashboard's greeting row, one-for-one: eyebrow pill, title,
          a context line, and the actions pushed to the far edge. The
          manual opens the way the product does. */}
      <section className="section-hero border-b border-line bg-surface pt-[72px]">
        <span className="hero-aurora" aria-hidden="true" />
        <span className="shape shape--arc shape--md shape--tr" aria-hidden="true" />

        <div className="container-page relative py-10">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-0">
              <span className="eyebrow accent-brand">
                <BookOpen className="h-3.5 w-3.5" />
                User manual · v1.3
              </span>
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-content sm:text-4xl">
                WASLX <span className="display-accent">documentation</span>
              </h1>
              <p className="mt-2 max-w-2xl text-content-muted">
                Everything agents, team leads and admins need — written in everyday language, with
                the real interface beside every explanation.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={handlePrint} className="btn btn-secondary btn--sm focus-ring">
                <Printer className="h-4 w-4" />
                Print
              </button>
              <Link to="/contact" className="btn-pill btn-pill--primary btn-pill--sm">
                Talk to support
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* ── Sticky seam ─────────────────────────────────────────────────
          A 3px progress rule plus the reader's position. On a page this
          long the depth cue has to be continuous, not on demand — which is
          also why the phone gets a chip track here rather than a dropdown:
          a closed dropdown shows you nothing about where you are. */}
      <div className="docs-seam surface-blur sticky top-[72px] z-40 border-b border-line">
        <div className="docs-progress-track">
          <div className="docs-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <div className="container-page hidden items-center justify-between py-2 lg:flex">
          <span className="text-ui-xs text-content-muted">
            Section {active.num} of {TOC.length}
          </span>
          <span className="text-ui-xs font-bold text-content">{active.label}</span>
        </div>

        <nav className="docs-chiprail lg:hidden" aria-label="Sections">
          {TOC.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => scrollTo(t.id)}
              className={`docs-chip focus-ring ${t.id === activeId ? 'is-active' : ''}`}
            >
              {t.num}. {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      {/* Platform switcher. The manual covers two clients that share most
          of their concepts but almost none of their screens, and stacking
          both in one scroll meant an agent on the phone read six web
          sections first. */}
      <div className="container-page pt-8">
        <div className="platform-tabs" role="tablist" aria-label="Platform">
          {PLATFORMS.map(pf => {
            const Icon = pf.icon;
            const active = platform === pf.id;
            return (
              <button
                key={pf.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPlatform(pf.id)}
                className={`platform-tab focus-ring ${active ? 'is-active' : ''} ${pf.soon ? 'is-soon' : ''}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{pf.label}</span>
                <span className="platform-tab__note">{pf.note}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="container-page py-8">
        <div className="docs-shell">

          {/* The rail stands on the app's NAV plane — dark in both themes,
              with its own on-colour scale. It is the single strongest cue
              that this manual and the product are one system. */}
          <aside className="hidden lg:block">
            <div className="docs-rail-stack">
              {/* The contents list is the home page's .uc-tab rail — same
                  component, same active treatment: a hairline-quiet row that
                  gains the section's own hue as a bar down its left edge when
                  it is the one you are reading. The rail used to stand on the
                  dark nav plane, which made the manual's navigation the single
                  heaviest object on a page whose job is to be read. */}
              <nav className="docs-toc" aria-label="Contents">
                <span className="docs-toc__eyebrow">Contents</span>

                <label className="docs-toc__search">
                  <Search className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Filter sections"
                    aria-label="Filter sections"
                  />
                </label>

                <div className="docs-toc__scroll">
                  {visible.map(t => {
                    const isActive = t.id === activeId;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => scrollTo(t.id)}
                        aria-current={isActive ? 'true' : undefined}
                        className={`focus-ring uc-tab ${accentFor(t.num)} ${isActive ? 'is-active' : ''} ${
                          !isActive && t.num < active.num ? 'is-read' : ''
                        }`}
                      >
                        <span className="docs-toc__num" aria-hidden="true">{t.num}</span>
                        <span className="min-w-0">{t.label}</span>
                      </button>
                    );
                  })}
                  {visible.length === 0 && (
                    <p className="docs-toc__empty">No section matches “{query}”.</p>
                  )}
                </div>

                <div className="docs-meter">
                  <div className="docs-meter__top">
                    <span className="docs-meter__label">Progress</span>
                    <span className="docs-meter__value">{Math.round(progress)}%</span>
                  </div>
                  <div className="docs-meter__track">
                    <span className="docs-meter__fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </nav>
            </div>
          </aside>

          {/* The paper. One borderless pillow carrying the whole manual,
              the same surface the dashboard's .soft-card panels use. */}
          <main ref={contentRef} className="docs-paper min-w-0">

            {/* Index of the module reference, on the first fold — the detail
                section itself sits three screens down. */}
            {/* The web module index — dashboard screens and a pointer to
                the Android tab. Both are Web-tab furniture: on the Android
                tab it was showing web screenshots above the phone guide,
                and pointing at the tab the reader was already on. */}
            {platform === 'web' && <ModuleGallery />}


            {show('intro') && (
              <Section
                id="intro"
                title="1. Introduction"
                open={isSectionOpen('intro')}
                onToggle={toggleSection}
              >
            {/* 1. Introduction */}
            <H3>1.1 What is WASLX?</H3>
            <P>
              <strong>WASLX</strong> helps your company run <strong>calling campaigns</strong>, keep <strong>customer lists (leads)</strong> organised, and see <strong>what is happening on the floor in near real time</strong>.
            </P>
            <P>You use it in two main ways:</P>
            <UL items={[
              'WASLX on the web — the browser screen for people in the office: setup, reports, user accounts, campaigns, billing (if your company uses it), and settings.',
              'WASLX on your phone (Android) — the app for agents to make calls, see leads, set follow-ups, wrap up calls with the right outcome, and check their own results.',
            ]} />
            <P>Behind the scenes, both the website and the app connect to the <strong>same WASLX service</strong> your company hosts or subscribes to, so information stays in sync.</P>

            <H3>1.2 Who does what?</H3>
            <Table
              head={['Who', 'Usually Uses', 'Typical Job']}
              rows={[
                ['Agent', 'Phone app (sometimes the website too)', 'Calls leads, fills in outcomes after calls, books follow-ups'],
                ['Admin / Supervisor', 'Website', 'Sets up users, campaigns, lists, watches live activity'],
                ['Platform support (rare)', 'Website — Organizations & Switch organization', 'Looks after multiple companies on one WASLX system — only special accounts'],
              ]}
            />

            <Hr />

              </Section>
            )}

            {show('getting-started') && (
              <Section
                id="getting-started"
                title="2. Getting Started"
                open={isSectionOpen('getting-started')}
                onToggle={toggleSection}
              >
            {/* 2. Getting Started */}
            <H3>2.1 WASLX on the Web</H3>
            <OL items={[
              'Open your WASLX website (the address your admin gave you) in a normal browser — Chrome, Edge, or Firefox work well.',
              'Click Login (or Sign in). Enter the email and password your admin created for you.',
              'If your company allows self-sign-up, use Register and follow the steps.',
              'If you forgot your password, use Forgot password, check your email for a code, then enter it on the verify code screen to set a new password.',
              'If you support more than one company on WASLX, you may be asked to pick which company you are working in before the main screen opens.',
              'After login you normally land on the Dashboard (home).',
            ]} />
            <Note>
              <strong>Renewal reminder:</strong> If your subscription is ending soon, a banner may appear at the top. You can close it for this session; to actually renew or change plan, go to Billing (if you have access) or ask your admin.
            </Note>

            <H3>2.2 WASLX on Your Phone (Android)</H3>
            <OL items={[
              'Install the app the way your company tells you (app store link, internal store, or shared install file).',
              'First time only: if the app asks for server address or company server link, type exactly what your IT person or admin wrote down for you. A typo here stops everything from working.',
              'The app may ask you to set WASLX as your default phone app. Say Yes if you want calls to flow smoothly through WASLX. You can change this later in Android settings.',
              'Sign in with the username/email and password from your admin.',
              'You may need to choose which campaign(s) you work on today. Pick the ones your supervisor assigned.',
            ]} />
            <H4>Using the app without signing in (guest mode)</H4>
            <P>
              If you open the app <strong>without</strong> logging in, you still get a simple phone experience: recent calls, dial pad, contacts, history, and basic settings. That is fine for <strong>personal dialing</strong>, but it is <strong>not</strong> connected to your company's leads and campaigns. To work as an agent, <strong>sign in</strong>.
            </P>

            <Hr />

              </Section>
            )}

            {show('modules') && (
              <Section
                id="modules"
                title="3. Module Reference — Every Screen, Explained"
                open={isSectionOpen('modules')}
                onToggle={toggleSection}
              >
            {/* 3. Module reference — the screens themselves */}
            <P>
              This section walks through the product screen by screen: what each module is for, who
              works in it, and what you can do there. The screenshots are the real interface.
            </P>
            <Note>
              Every record shown in these screenshots is a demo value. Names, email addresses and
              mobile numbers are replaced before capture, so nothing on this page belongs to a real
              person.
            </Note>
            <ModuleShowcase />

            <Hr />

              </Section>
            )}

            {show('web-admins') && (
              <Section
                id="web-admins"
                title="4. WASLX on the Web — For Managers and Admins"
                open={isSectionOpen('web-admins')}
                onToggle={toggleSection}
              >
            {/* 4. Web — Admins */}
            <H3>3.1 Where to Find Things (Left Menu)</H3>
            <Table
              head={['Menu Name', 'What It Is For']}
              rows={[
                ['Dashboard', 'Live picture of calls and agents'],
                ['User Management', 'People who log in, their roles, groups, teams, bulk upload'],
                ['Campaign Management', 'Campaigns, agents, lists, call history per campaign'],
                ['Lead Management', 'All leads, lists, import from spreadsheet, custom fields'],
                ['Billing', 'Plan and payments (if your company uses this)'],
                ['Organizations', 'Only for special platform accounts — list of companies'],
                ['Switch organization', 'Same — jump to another company\'s data to help them'],
                ['Analytics', 'Charts and history-style reports'],
                ['Support', 'Help and contact'],
                ['Bell icon (top)', 'Your notifications'],
                ['Settings', 'Skills, Break reasons, Issues, Departments, Dispositions'],
                ['Logout', 'Sign out completely'],
              ]}
            />

            <H3>3.2 Dashboard — Live Picture</H3>
            <P>Open <strong>Dashboard</strong> from the left menu. This screen <strong>updates by itself every few seconds</strong>. You can also press <strong>Refresh data</strong> for an instant update.</P>
            <UL items={[
              'Top row: How many calls are active, ringing, waiting in queue, or in the phone menu (IVR), and how many agents are logged in.',
              'Second row: Agents idle, on a call, ready, finishing after-call (disposition), on break, and dead calls.',
              'Live call list: A table of calls happening now. Use the search box to narrow by campaign, number, or status. Export CSV downloads a spreadsheet.',
              'Agent list: Shows who is on which campaign, status, and timing. You can filter by one campaign and export that list.',
            ]} />
            <Note>If you see "Could not load realtime report", press Try again. If it keeps failing, your internet or WASLX server may be down — see Section 8.</Note>

            <H3>3.3 User Management</H3>
            <P>Here you <strong>add people</strong>, <strong>edit their details</strong>, <strong>turn off</strong> old accounts, and put people into <strong>roles</strong>.</P>
            <Note><strong>Two different "password" situations:</strong> "I forgot my password" on the login page lets any user start email reset themselves. An admin with User Management rights can set a new password for another person from inside User Management — this is not the same as "forgot password".</Note>

            <H3>3.4 Campaign Management</H3>
            <P>Campaigns are the <strong>packages of work</strong>: which list, which agents, which rules, which outcomes (dispositions) apply. Creating or editing a campaign usually follows a wizard:</P>
            <OL items={[
              'Basics — name, whether the campaign is mostly outbound, inbound, or both, how dialing should behave (manual vs automatic), short description.',
              'Rules — prefix for dialing, how dispositions work, cool-off time, how many tries before giving up. Dispositions themselves are created under Settings → Dispositions first.',
              'Later steps — usually agents and lead lists; complete everything required before saving.',
              'Finish — save the campaign.',
            ]} />
            <P><strong>Starting and stopping:</strong> If your account allows it, you will see actions to start, stop, pause, or resume a campaign.</P>
            <P><strong>Call logs for one campaign:</strong> On the campaign row, open Call logs to see history for that campaign. Listening to or downloading a recording may only appear if your role allows it.</P>

            <H3>3.5 Lead Management</H3>
            <P>Leads are the people or companies you call. Here you can:</P>
            <UL items={[
              'Search and filter (status, list, dates, pages of results).',
              'Add or edit a lead (name, phone, email, company, source, status, which list it belongs to, and any extra fields your company added).',
              'Delete or recycle leads — only if your role allows.',
              'Assign leads to agents or lists when the buttons are available.',
            ]} />
            <H4>Importing from Excel</H4>
            <OL items={[
              'Start import and choose your file.',
              'WASLX shows column headers, sample rows, and asks you to match each column to a WASLX field.',
              'Choose what to do with duplicates (same number already in the system).',
              'Run the import and read the summary: imported, skipped, errors.',
            ]} />

            <H3>3.6 Call Logs (from the Website)</H3>
            <P>The easiest place for supervisors is <strong>Campaign Management → Call logs</strong> on a specific campaign. Depending on your access, you may also export data, play or download call recordings, or in rare cases manually correct a record. Manual changes should follow your company's compliance rules.</P>

            <H3>3.7 Analytics</H3>
            <P>Analytics shows <strong>history and summaries</strong>. For live numbers, use the Dashboard first.</P>

            <H3>3.8 Billing</H3>
            <P>If your company pays for WASLX through the product, you can see your current plan and status, and change plan via a secure payment screen. If you do not see Billing, your role does not include it — ask your admin.</P>

            <H3>3.9 Notifications</H3>
            <P>Click the <strong>bell</strong> at the top → Notifications. Open items to read them; mark them read as the screen suggests.</P>

            <H3>3.10 Settings — Skills, Break Reasons, Issues, Departments, Dispositions</H3>
            <Table
              head={['Item', 'Plain Meaning']}
              rows={[
                ['Skills', 'Tags or skills used for people or routing.'],
                ['Break reasons', 'Why an agent went on break (lunch, training, etc.).'],
                ['Issues', 'Types of problems for support workflows.'],
                ['Departments', 'Your org structure for reporting or campaigns.'],
                ['Dispositions', 'Call outcomes — set these up before you build campaigns that need them.'],
              ]}
            />

            <H3>3.11 Organizations (Special Accounts Only)</H3>
            <P>If you see <strong>Organizations</strong>, you are in a platform-level account. You can list all companies on that WASLX installation and use <strong>Switch organization</strong> to work inside another company's screens to help them.</P>
            <Warn><strong>Important:</strong> This level of access is very powerful. Only use it when your job requires it, and follow your company's security policy.</Warn>

            <Hr />

              </Section>
            )}

            {show('extra-fields') && (
              <Section
                id="extra-fields"
                title="5. Extra Fields on Leads"
                open={isSectionOpen('extra-fields')}
                onToggle={toggleSection}
              >
            {/* 4. Extra Fields */}
            <P>Your admin can add <strong>custom fields</strong> so each lead carries more than the basics.</P>
            <UL items={[
              'On the website: Lead Management → manage custom fields → add label, type, and whether it is required. For dropdown fields, list the choices.',
              'When importing: Match spreadsheet columns to those fields in the import mapper. Use the short names your admin gave you.',
              'On the agent phone: After sync, Lead and Edit lead screens show the same extra fields when your company saved data for that lead.',
              'Behind the scenes: Some behavior (recording on/off, dialing style, etc.) is set per company or per campaign by your admin or IT — agents do not edit that here.',
            ]} />

            <Hr />

              </Section>
            )}

            {show('integrations') && (
              <Section
                id="integrations"
                title="6. How WASLX Works with Other Systems"
                open={isSectionOpen('integrations')}
                onToggle={toggleSection}
              >
            {/* 5. Integrations */}
            <P>What you notice day to day:</P>
            <Table
              head={['You notice…', 'What it usually means']}
              rows={[
                ['Payment screen when registering or changing plan', 'Secure checkout; if payment does not finish, the plan may not change'],
                ['Notifications on the phone', 'Your company can send alerts or auto-dial requests to agents — you must allow notifications in Android when asked'],
                ['Call recordings in the website', 'Recordings are stored securely in the cloud; who can play or download them is controlled by your admin'],
                ['Password reset emails', 'Come from your company\'s mail setup; check spam if missing'],
                ['Big user import "still running"', 'Very large files may finish in the background — wait and refresh'],
              ]}
            />
            <Note><strong>Do Not Call (DNC):</strong> Numbers on your company's do-not-call list must not be dialed. If a person asks to be removed, follow your script and tell your admin so the number is added to DNC.</Note>

            <Hr />

              </Section>
            )}

            {show('phone-agents') && (
              <>
            {/* 6. Phone — Agents */}
            <H2 id="phone-agents">7. The WASLX Android App</H2>
            <P>
              The Android app is what an agent works in all day: it dials, it carries the customer
              list, it takes tasks and messages, and it keeps working when the signal drops. Admin
              configuration — users, campaigns, trunks, reporting — stays on the web dashboard
              covered in section 4.
            </P>
            <P>
              Every screen below was captured on a real handset, not an emulator or a mockup.
              Customer mobile numbers are blurred; nothing else is altered.
            </P>

            <H3>7.1 Why the App Asks for Access</H3>
            <Table
              head={['Topic', 'Why WASLX Asks']}
              rows={[
                ['Internet', 'Talks to your company\'s WASLX server'],
                ['Phone / calls', 'To place and manage calls'],
                ['Call history / phone state', 'Recent calls and which SIM on a two-SIM phone'],
                ['Contacts', 'To show names and save numbers where allowed'],
                ['Photos / media / files (audio)', 'On some phones, to find call recordings saved by the phone\'s own recorder'],
                ['Run in background / sync', 'So your status (online / busy) stays reasonably up to date'],
                ['Notifications', 'So you see assignments and reminders'],
                ['Stay awake / full screen', 'So important call screens can appear on time'],
                ['Show over other apps (optional)', 'For a caller pop-up on top of other apps — only if you agree in system settings'],
              ]}
            />
            <Note>If you deny phone or call-log access, caller ID, recents, or after-call steps may not work well.</Note>

            <H3>7.2 Default Phone App and During the Call</H3>
            <P>If WASLX is your <strong>default phone app</strong>, tapping phone numbers in other apps can open WASLX, and in-call screens stay consistent. You can pick <strong>which SIM</strong> to use when the phone asks or when WASLX offers a choice.</P>

            <H3 id="android-modules">7.3 Every Screen in the App</H3>
            <P>
              The app opens on <strong>Home</strong>, and everything else lives behind the{' '}
              <strong>menu button in the top-left corner</strong> — Notifications, Customers, SIP
              Call, Tasks, Messages, SMS, Support and Offline &amp; Sync, with Edit profile and Log
              out at the bottom. There is no bottom tab bar.
            </P>
            <Note>
              Which entries you see depends on your role. The reference below was captured on a real
              handset signed in as an <strong>agent</strong>, which is the role the app is built
              around — admin configuration stays on the web dashboard.
            </Note>

            <AndroidShowcase />

            <H3>7.4 Making a Call — Simple Flow</H3>
            <OL items={[
              'Open Dialer (from Home or the bottom menu).',
              'Type the number or pick a contact. On two-SIM phones, choose the correct line if asked.',
              'Talk.',
              'When the call ends, complete post-call disposition (outcome) — your campaign may require this before you move on.',
              'Recordings: Some phones save a recording file; WASLX may upload it if your campaign allows — follow local law and company consent rules.',
            ]} />

            <H3>7.5 Showing as Online or Busy</H3>
            <P>The app sends <strong>occasional updates</strong> so supervisors see roughly whether you are available. If they say you look offline, open WASLX on Wi-Fi or good data for a minute, and check that battery saver is not blocking the app.</P>

            <Hr />

              </>
            )}

            {show('safety') && (
              <>
            {/* 7. Safety */}
            <H2 id="safety">8. Staying Safe and Following the Rules</H2>
            <UL items={[
              'Sign out on shared PCs or shared tablets when done (Logout on the web; Logout in the app).',
              'If you do not see a menu, your account may not be allowed that action — ask your admin instead of sharing passwords.',
              'Exports (spreadsheets of leads or calls) contain personal data — only download when your job needs it, and store files safely.',
              'Recordings: only make or play recordings where the law and your company allow.',
              'DNC: never argue — honor the list and the customer\'s request.',
            ]} />

            <Hr />

              </>
            )}

            {show('troubleshoot') && (
              <>
            {/* 8. Troubleshooting */}
            <H2 id="troubleshoot">9. When Something Goes Wrong</H2>
            <H3>On the Website</H3>
            <Table
              head={['Problem', 'Try This']}
              rows={[
                ['Login fails', 'Check the website address, caps lock, and password; use Forgot password if needed'],
                ['Kicked back to login', 'Session ended — sign in again'],
                ['A menu item is missing', 'Your role does not include it — ask your admin'],
                ['Dashboard stays blank', 'Check internet / VPN; try another browser; ask IT if WASLX server is up'],
                ['Import shows many errors', 'Compare your file to the sample layout; fix column names; re-map fields'],
                ['Payment or plan will not change', 'Finish the payment window; if it still fails, contact your support email'],
              ]}
            />
            <H3>On the Phone</H3>
            <Table
              head={['Problem', 'Try This']}
              rows={[
                ['"Cannot reach server"', 'Re-enter server address carefully; try Wi-Fi vs mobile data; ask IT if the server allows your network'],
                ['No notifications / no auto-dial', 'Turn notifications on for WASLX; turn off battery restrictions for WASLX; reinstall only if IT asks'],
                ['Wrong SIM', 'Set default outgoing SIM in Android settings; use WASLX\'s SIM choice when shown'],
                ['After-call screen never appears', 'Make sure the call ended normally; update the app; ask supervisor if campaign requires disposition'],
                ['Stuck in guest mode', 'Sign in'],
                ['Recordings not uploading', 'Allow storage / audio permission if asked; confirm your campaign uses recordings'],
              ]}
            />

            <Hr />

              </>
            )}

            {show('examples') && (
              <>
            {/* 9. Examples */}
            <H2 id="examples">10. Real-Life Examples (Step by Step)</H2>
            {[
              {
                title: 'Launching a new campaign',
                steps: [
                  'Admin: create Dispositions (and Departments / Skills if needed).',
                  'Run Campaign wizard — attach lists and agents.',
                  'Start campaign.',
                  'Agents: open app → sign in → pick that campaign → test one call.',
                ],
              },
              {
                title: 'Importing many leads with extra fields',
                steps: [
                  'Define custom fields.',
                  'Prepare Excel columns to match.',
                  'Lead Management import → map columns → choose duplicates rule → import.',
                  'Check counts → link list to campaign.',
                ],
              },
              {
                title: 'New phone for an agent',
                steps: [
                  'Install app → enter server address.',
                  'Sign in → accept permissions → set default phone app if required.',
                  'Ask supervisor to send a test notification or test call.',
                ],
              },
              {
                title: 'Watching the queue',
                steps: [
                  'Open Dashboard.',
                  'Watch calls in queue and live list.',
                  'Filter agents by campaign.',
                  'Export if you need a file for a meeting.',
                ],
              },
              {
                title: 'Checking one call with a recording',
                steps: [
                  'Campaign Management → Call logs for that campaign.',
                  'Find the row → Play / download if your role allows.',
                ],
              },
              {
                title: 'Someone must not be called',
                steps: [
                  'Stop dialing; tell admin to add the number to DNC.',
                  'Update the lead in WASLX so nobody calls again by mistake.',
                ],
              },
              {
                title: 'Upgrading the company plan',
                steps: [
                  'Someone with Billing access → Change plan → complete payment.',
                  'Sign out and in again if things look stuck.',
                ],
              },
              {
                title: 'Testing what a new role can see',
                steps: [
                  'Admin creates a test user with the new role.',
                  'Logs in as that user → notes missing pages.',
                  'Adjusts role until correct → documents for rollout.',
                ],
              },
              {
                title: 'Helping another company (platform only)',
                steps: [
                  'Organizations → Switch organization.',
                  'Work carefully inside their view; do not change billing unless authorized.',
                ],
              },
            ].map((ex, i) => (
              <div key={i} className="mb-5 rounded-xs bg-well p-5">
                <p className="mb-3 flex items-center gap-2.5 font-bold text-content">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-pill bg-primary-surface font-mono text-2xs text-on-primary">
                    {i + 1}
                  </span>
                  {ex.title}
                </p>
                <ol className="space-y-1 pl-8">
                  {ex.steps.map((step, si) => (
                    <li key={si} className="list-decimal text-content-muted marker:font-bold marker:text-primary">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}

            <Hr />

              </>
            )}

            {show('permissions') && (
              <>
            {/* 10. Permissions */}
            <H2 id="permissions">11. Who Can Do What (Simple View)</H2>
            <P>Your admin bundles permissions into roles (for example "Agent", "Supervisor", "Billing"). Use this table to know whom to ask.</P>
            <Table
              head={['I need to…', 'Ask…']}
              rows={[
                ['Reset my password', 'Use Forgot password on login'],
                ['Reset someone else\'s password', 'Admin with user-management rights'],
                ['Create campaigns or change lists', 'Admin / supervisor'],
                ['Import ten thousand leads', 'Admin (may take a few minutes)'],
                ['Listen to call recordings', 'Supervisor (if policy allows)'],
                ['Change subscription or card', 'Billing role or company owner'],
                ['See another company\'s data', 'Platform support only — rare'],
              ]}
            />
            <Note>If a button is missing, assume your role is not meant to use it — that is normal, not a bug.</Note>

            <Hr />

              </>
            )}

            {show('glossary') && (
              <>
            {/* 11. Glossary */}
            <H2 id="glossary">12. Word List</H2>
            <Table
              head={['Word', 'Meaning']}
              rows={[
                ['Campaign', 'A calling program: lists, agents, rules, outcomes.'],
                ['Lead', 'One person or business record to call (phone number + details).'],
                ['Lead list', 'A named group of leads you attach to campaigns.'],
                ['Disposition', 'The outcome of a call (answered, no answer, sale, callback…).'],
                ['Follow-up', 'A planned next step (often shown on the phone calendar).'],
                ['DNC', 'Do Not Call — numbers your company must not dial.'],
                ['Dashboard', 'Live operations screen in the browser.'],
                ['Analytics', 'More historical views and charts.'],
                ['Guest mode', 'Phone app without login — basic dialer only.'],
                ['Superadmin / platform', 'People who manage multiple companies on one system.'],
                ['Webhook', 'An automatic message one computer system sends to another when something happens (no human clicking "Send").'],
                ['Integration credential', 'A secret key or token only IT should hold — not the same as your login password.'],
              ]}
            />

            <Hr />

              </>
            )}

            {show('custom-module') && (
              <>
            {/* 12. Custom Module */}
            <H2 id="custom-module">13. Custom "Module" — Shaping WASLX to Your Company</H2>
            <P>In everyday language, a <strong>custom module</strong> does not mean you install a separate mystery program. It means <strong>your company's data and rules are layered on top of WASLX</strong> so it behaves like <em>your</em> process, not a generic template.</P>

            <H3>12.1 What "a New Module" Usually Means for Staff</H3>
            <P>Managers often ask for a new module when they want a <strong>new place in WASLX</strong> to add, list, open, change, and remove (or archive) records of a new kind — for example "Warranty claims", "Partner referrals", or "Site visit tickets".</P>
            <Note>
              <strong>"Custom module"</strong> = either (A) extra field types and screens on an existing area (leads first), or (B) a brand-new menu delivered as a custom project by your IT team or WASLX partner.
            </Note>

            <H3>12.2 Field Types You Can Mix</H3>
            <Table
              head={['Type', 'Good For']}
              rows={[
                ['Short text', 'Names, short codes, one-line answers'],
                ['Long text / paragraph', 'Notes, descriptions'],
                ['Number', 'Amounts, scores, counts'],
                ['Date', 'Follow-up date, birth date, contract date'],
                ['Yes / No (toggle)', 'Opt-in flags'],
                ['Email', 'Valid email shape'],
                ['Phone', 'Phone-style entry'],
                ['Website link', 'URLs to a brochure or ticket system'],
                ['Dropdown (choices)', 'Fixed lists your admin maintains (region, product line, priority)'],
              ]}
            />
            <Note>Required fields block Save until filled in. Inactive fields can be hidden from new entry while old data stays for history.</Note>

            <H3>12.3 Your Company's Word Lists (Small but Powerful)</H3>
            <Table
              head={['You set up…', 'What it changes for staff']}
              rows={[
                ['Dispositions', 'The outcome buttons after a call (sale, callback, wrong number…).'],
                ['Break reasons', 'What agents pick when they step away.'],
                ['Skills', 'How you tag people or skills for routing or reporting.'],
                ['Departments', 'How you split teams or reporting lines.'],
                ['Issues (under Settings)', 'The types of requests people see on the Support form.'],
              ]}
            />

            <H3>12.4 Bigger Changes — A Real "New Menu" Module</H3>
            <P>If you need entirely new screens (your own list + form + search with many field types), special approvals, or rules nobody else uses, that is a project. Your partner will:</P>
            <UL items={[
              'Name the module ("Referrals", "Quality checks", …).',
              'Decide who may create, edit, or delete rows (roles).',
              'Choose the field types and any links to leads or campaigns.',
              'Optionally add webhooks so another system stays in sync.',
            ]} />

            <Hr />

              </>
            )}

            {show('webhooks') && (
              <>
            {/* 13. Webhooks */}
            <H2 id="webhooks">14. Web Forms, Credentials, Webhooks, and Other Integrations</H2>
            <P><strong>Integration</strong> means WASLX and another tool share work — a payment page, email, your phone, a spreadsheet, or a CRM.</P>

            <H3>13.1 Support Request on the Website (Built-in Web Form)</H3>
            <P>Anyone can open the <strong>Support</strong> page from the website. The form usually asks for:</P>
            <UL items={[
              'Summary type — a drop-down built from Settings → Issues.',
              'Email — required if the person is not logged in, so support can reply.',
              'Subject — short title.',
              'Message — details of the problem or request.',
            ]} />
            <Note><strong>Tip for admins:</strong> Keep Issues up to date so the Summary type list matches the kinds of tickets you really want (billing, access, campaign help, etc.).</Note>

            <H3>13.2 "Contact Us" from the Phone App</H3>
            <P>In the app's <strong>Settings</strong>, Contact us sends a message through the same kind of support path as the website. Use it when you are on the road and cannot open the browser.</P>

            <H3>13.3 Two Kinds of "Passwords" — People vs. Machines</H3>
            <Table
              head={['Kind', 'What It Is', 'Rules for Everyone']}
              rows={[
                ['People credentials', 'Login email + password (and sometimes server address on the phone)', 'Pick strong unique passwords; never share your login; use Forgot password instead of telling someone your password; sign out on shared PCs.'],
                ['Integration credentials', 'Secret keys, API tokens, payment keys, "webhook signing secrets" — strings that let software talk to software', 'Only IT / vendor should create, store, or rotate these. Never put them in WhatsApp, email, screenshots, or this manual. If a key leaks, treat it like a stolen house key: revoke and replace immediately.'],
              ]}
            />
            <Warn><strong>For managers:</strong> If a vendor asks for your personal WASLX password, say no — they should use a separate service account with limited rights.</Warn>

            <H3>13.4 Webhooks in Plain Language</H3>
            <P>A <strong>webhook</strong> is an <strong>automatic message between servers</strong>: when something specific happens in WASLX (or in your other system), the other side receives a small structured message right away, without a person clicking "Export".</P>
            <Table
              head={['Direction', 'Plain Explanation']}
              rows={[
                ['WASLX → your system (outbound)', 'WASLX calls a web address (URL) your IT gives — for example "new lead created" — and sends a packet of fields your other app can read.'],
                ['Your system → WASLX (inbound / API)', 'Your website or CRM pushes data into WASLX when a visitor submits a form (your IT uses a secure server-to-server path, not a password typed on a public page).'],
              ]}
            />
            <P><strong>Safety habits your IT should enforce:</strong></P>
            <UL items={[
              'HTTPS only (encrypted web address), not plain http.',
              'A shared signing secret or signature so each message proves it really came from WASLX, not a random stranger on the internet.',
              'Retry rules if the other side is down for a minute — so data is not lost silently.',
              'No secret keys inside the body of public forms; keys stay on the server, never in the browser.',
            ]} />

            <H3>13.5 Bringing Leads in from Outside</H3>
            <UL items={[
              'File import (Excel): Managers use Lead Management → Import — a guided upload inside WASLX.',
              'A public "Contact us" on your marketing website: Your web team usually posts submissions securely into WASLX or into a sheet you import — that is custom integration.',
              'WASLX Support form is for people using WASLX who need help, not for anonymous sales leads — unless IT builds something separate on purpose.',
            ]} />

            <H3>13.6 Quick Checklist — "Is This Built-in or Custom?"</H3>
            <Table
              head={['I want…', 'Usually…']}
              rows={[
                ['Staff to ask for IT help with WASLX', 'Support web form or app Contact us'],
                ['Extra questions on each lead', 'Custom fields (Sections 4 and 12)'],
                ['A new list + forms + many field types under its own menu', 'Custom module project (Section 12)'],
                ['Another system updated the moment something happens', 'Webhook / automation project + IT-held credentials'],
                ['Leads from a marketing website into WASLX', 'Custom integration'],
                ['Pay or renew WASLX', 'Billing or Register'],
              ]}
            />

            <Hr />

            {/* CTA */}
            <div className="accent-brand card card--tint mt-4 text-center">
              <span className="eyebrow">Need more help?</span>
              <h3 className="mt-3 text-xl font-bold text-content">Talk to the WASLX team</h3>
              <p className="mx-auto mt-2 max-w-md text-[0.9375rem] text-content-muted">
                Our team responds within one business day. No ticket queues — you talk to the people
                building the product.
              </p>
              <Link to="/contact" className="btn-pill btn-pill--primary mt-6">
                Contact support
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

              </>
            )}

            {/* Not inside any section wrapper: those are gated by the
                platform predicate, which is false for every section on the
                iOS tab — so a panel placed inside one could never render. */}
            {platform === 'ios' && <IosComingSoon />}
          </main>
        </div>
      </div>

      {/* Only once the reader is genuinely deep. A jump-to-top on a page
          you have barely scrolled is decoration. */}
      {progress > 6 && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="docs-totop focus-ring"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      <Footer />
    </div>
  );
};

export default Documentation;
